// === src/pages/dashboard/Dashboard.jsx ===
import React, { useState, useEffect, useRef, Suspense } from "react";
import Sidebar from "../../components/Sidebar";
import Loader from "../../components/Loader";
import { Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  FaCar,
  FaRoute,
  FaChartLine,
  FaCog,
  FaBars,
  FaBell,
  FaUserCircle,
  FaMapMarkerAlt
} from "react-icons/fa";
import CountUp from "react-countup";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import "../../styles/transition.css";

const Dashboard = () => {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("sidebarCollapsed") === "true");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ vehicles: 0, trips: 0, fuel: 0 });
  const { user } = useAuth();
  const nodeRef = useRef(null);

  const navItems = [
    { name: "Vehicles", path: "vehicles", icon: <FaCar className="text-xl" /> },
    { name: "Live Map", path: "map", icon: <FaMapMarkerAlt className="text-xl" /> },
    { name: "Trips", path: "trips", icon: <FaRoute className="text-xl" /> },
    { name: "Reports", path: "reports", icon: <FaChartLine className="text-xl" /> },
    { name: "Settings", path: "settings", icon: <FaCog className="text-xl" /> },
  ];

  useEffect(() => {
    setTimeout(() => {
      setStats({ vehicles: 124, trips: 36, fuel: 475 });
      setLoading(false);
    }, 300);

    const interval = setInterval(() => {
      setStats({
        vehicles: 124,
        trips: 36 + Math.floor(Math.random() * 5),
        fuel: 475 + Math.floor(Math.random() * 10),
      });
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname]);

  const skeletonBox = "animate-pulse bg-gray-200 dark:bg-gray-700 h-20 rounded";

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      <Sidebar
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        navItems={navItems}
        onLogout={() => console.log("Logging out...")}
      />

      {mobileOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 md:hidden" onClick={() => setMobileOpen(false)}>
          <div
            className="w-64 bg-gray-100 dark:bg-gray-800 p-0 shadow h-full"
            onClick={(e) => e.stopPropagation()}
          >
            <Sidebar
              mobile
              collapsed={false}
              setCollapsed={() => {}}
              navItems={navItems}
              onLogout={() => console.log("Mobile logout")}
              onClose={() => setMobileOpen(false)}
            />
          </div>
        </div>
      )}

      <main className="flex-1 px-4 pb-10 bg-white dark:bg-gray-900 text-gray-900 dark:text-white relative">
        {(loading) && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/80 dark:bg-black/60 rounded">
            <Loader />
          </div>
        )}

        <div className={`${loading ? "opacity-30 pointer-events-none" : ""}`}>
          <header className="flex justify-between items-center py-4 border-b border-gray-200 dark:border-gray-700 mb-6">
            <div className="md:hidden">
              <button onClick={() => setMobileOpen(true)} className="text-2xl text-orange-500">
                <FaBars />
              </button>
            </div>
            <div className="flex items-center gap-4">
              <FaBell className="text-xl text-orange-500 animate-pulse" />
              <span className="text-sm">Welcome, {user?.firstName || user?.email || "User"}</span>
              <FaUserCircle className="text-2xl" />
            </div>
          </header>

          <section className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {loading ? (
              <>
                <div className={skeletonBox}></div>
                <div className={skeletonBox}></div>
                <div className={skeletonBox}></div>
              </>
            ) : (
              <>
                <div className="bg-orange-100 dark:bg-orange-800 p-4 rounded shadow text-center hover:scale-105 transition-transform duration-300">
                  <h3 className="text-sm">Total Vehicles</h3>
                  <p className="text-3xl font-bold"><CountUp end={stats.vehicles} duration={1.2} /></p>
                </div>
                <div className="bg-green-100 dark:bg-green-800 p-4 rounded shadow text-center hover:scale-105 transition-transform duration-300">
                  <h3 className="text-sm">Active Trips</h3>
                  <p className="text-3xl font-bold"><CountUp end={stats.trips} duration={1.2} /></p>
                </div>
                <div className="bg-blue-100 dark:bg-blue-800 p-4 rounded shadow text-center hover:scale-105 transition-transform duration-300">
                  <h3 className="text-sm">Fuel Used Today</h3>
                  <p className="text-3xl font-bold"><CountUp end={stats.fuel} duration={1.2} /> gal</p>
                </div>
              </>
            )}
          </section>

          <Suspense fallback={<Loader />}>
            <TransitionGroup component={null}>
              <CSSTransition key={location.key} classNames="fade" timeout={300} nodeRef={nodeRef} unmountOnExit>
                <div ref={nodeRef}><Outlet /></div>
              </CSSTransition>
            </TransitionGroup>
          </Suspense>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
